import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Me } from '@/api/client';
import { Dashboard } from '@/pages/Dashboard';
import { Login } from '@/pages/Login';
import { PORTAL_COPY, pathForPortal, portalFromPath } from '@/lib/portal';
import { Button, Card, ErrorNote, Spinner } from '@/ui';

export default function App() {
  const portalKey = portalFromPath(window.location.pathname) ?? 'skyrun';
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);

  const loadMe = useCallback(async () => {
    setLoading(true);
    setAuthError(false);
    try {
      setMe(await api.me());
    } catch (err) {
      // Only a real 401 means signed out. A 500 or a network blip must not bounce an
      // established session to the sign-in screen.
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
      } else {
        setAuthError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  async function logout() {
    try {
      await api.logout();
    } finally {
      window.location.assign(pathForPortal(portalKey));
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading" />
      </div>
    );
  }
  if (authError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-sm p-5">
          <ErrorNote>Could not load {PORTAL_COPY[portalKey].name}</ErrorNote>
          <Button className="mt-4" onClick={() => void loadMe()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }
  if (!me) return <Login portal={portalKey} />;
  const portal = me.portals.find((p) => p.key === portalKey);
  if (!portal) {
    const home = me.portals[0];
    if (home) {
      window.location.replace(pathForPortal(home.key));
      return null;
    }
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-sm p-5">
          <ErrorNote>This account does not have access to any dashboards. Ask Besty to add you.</ErrorNote>
          <Button className="mt-4" onClick={() => void logout()}>
            Sign out
          </Button>
        </Card>
      </div>
    );
  }
  return <Dashboard key={portal.key} me={me} portal={portal} onLogout={() => void logout()} />;
}
